import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { submitForm } from "@/lib/api";

const skills = [
  "Video Editing (Premiere Pro, DaVinci Resolve, After Effects, etc.)",
  "AI Filmmaking & Generative AI Tools",
  "Cinematography & Camera Direction (in AI Filmmaking)",
  "Film Direction & Visual Storytelling",
  "Screenwriting & Creative Content Development",
  "Motion Graphics & Visual Design",
  "Creative Problem Solving and a passion for learning new technologies",
];

const roles = [
  "Video Editing",
  "AI Filmmaking",
  "Cinematography",
  "Film Direction",
  "Screenwriting",
  "Motion Graphics",
  "Other",
];

const initial = { name: "", email: "", phone: "", role: "", portfolio: "", message: "" };

export default function CareersPage() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const applyRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Trigger highlight glow after a brief delay
    const t = setTimeout(() => setHighlight(true), 600);
    // Remove highlight after animation completes
    const t2 = setTimeout(() => setHighlight(false), 3600);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone) || !form.role) {
      toast.error("Please fill your name, email or phone, and select a skill.");
      return;
    }
    setLoading(true);
    try {
      await submitForm("/careers", "career_applications", form);
      toast.success("Application received — we'll get back to you soon! 🎉");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-black text-white">
        {/* Hero */}
        <section className="px-6 pb-4 pt-40 md:px-10">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50">Careers</p>
              <h1 className="mt-4 font-display text-5xl font-normal tracking-tight sm:text-6xl md:text-7xl">
                Make things that <em className="font-serif italic">move people.</em>
              </h1>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/50">
                We're a small studio doing big work — AI films, brand ads and content that
                travels. We're hiring AI Filmmakers: hybrid creators who mix classic film
                craft with generative AI. If that's you, we want to hear from you.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Application form */}
        <section
          id="apply"
          ref={applyRef}
          data-testid="careers-form"
          className={`px-6 py-10 md:px-10 md:py-16 relative rounded-2xl transition-all duration-1000 ${
            highlight
              ? "ring-2 ring-white/40 shadow-[0_0_40px_rgba(255,255,255,0.15),0_0_80px_rgba(45,212,191,0.1)] bg-white/[0.03]"
              : "ring-0 ring-transparent shadow-none bg-transparent"
          }`}
          style={{ margin: highlight ? '0 8px' : '0' }}
        >
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-32">
              <div className="flex flex-col">
                <Reveal>
                  <h2 className={`font-display text-4xl font-normal tracking-tight sm:text-5xl transition-all duration-1000 ${
                    highlight ? "text-white [text-shadow:0_0_20px_rgba(255,255,255,0.6),0_0_40px_rgba(45,212,191,0.4),0_0_60px_rgba(45,212,191,0.2)]" : ""
                  }`}>Apply Now.</h2>
                  <p className="mt-4 text-sm text-white/50">
                    Just 4 quick fields — takes less than a minute ⚡
                  </p>
                </Reveal>

                <form onSubmit={submit} className="mt-10 flex flex-col gap-8">
                  {/* Name */}
                  <Field
                    label="What's your name?"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Ragul Kumar"
                    required
                  />

                  {/* Contact - stacked on mobile, side by side on desktop */}
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <Field
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="e.g. ragul@gmail.com"
                      hint="We'll reach out here"
                    />
                    <Field
                      label="Phone / WhatsApp"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="e.g. 98765 43210"
                      hint="Easier to connect? Drop your number"
                    />
                  </div>

                  {/* Role - big friendly select */}
                  <SelectField
                    label="What are you best at?"
                    value={form.role}
                    onChange={update("role")}
                    options={roles}
                    placeholder="Pick one skill you're strongest in"
                    required
                  />

                  {/* Portfolio - optional with hint */}
                  <Field
                    label="Got a portfolio or showreel?"
                    value={form.portfolio}
                    onChange={update("portfolio")}
                    placeholder="Paste a link (YouTube, Instagram, website...)"
                    hint="Optional — but helps us see your work"
                  />

                  {/* Message - optional, friendly */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-white/70">
                      Anything else you'd like to share?
                    </label>
                    <textarea
                      value={form.message}
                      onChange={update("message")}
                      rows={3}
                      placeholder="Tell us about yourself, your work, or just say hi 👋"
                      className="resize-none rounded-lg border border-white/10 bg-white/[0.04] px-4 py-4 text-base outline-none transition-all placeholder:text-white/25 focus:border-white/40 focus:bg-white/[0.06]"
                    />
                    <p className="text-xs text-white/30">Optional — no pressure!</p>
                  </div>

                  <div className="mt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="careers-submit"
                      className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-white px-10 py-5 text-sm font-bold tracking-wider text-black transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "SUBMIT APPLICATION"}
                      {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                    </button>
                  </div>
                </form>
              </div>

              {/* Right column: why join */}
              <div className="flex flex-col">
                <Reveal>
                  <h2 className="font-display text-4xl font-normal tracking-tight sm:text-5xl">Why Catalyst 7.</h2>
                </Reveal>
                <div className="mt-12 flex flex-col gap-12">
                  {[
                    ["Craft First", "We obsess over frames, cuts and sound. You'll work on projects where quality is the brief."],
                    ["AI-Native", "We build with the newest generative tools every single week — you'll always be at the edge."],
                    ["Small Team, Big Canvas", "No layers, no bureaucracy. Your work ships and gets seen — from brand films to viral shorts."],
                    ["Remote Friendly", "Work from anywhere. We care about the output, not the chair you sit in."],
                  ].map(([title, body], i) => (
                    <Reveal key={title} delay={0.1 * (i + 1)}>
                      <div className="flex flex-col gap-3 border-t border-white/10 pt-8 first:border-t-0 first:pt-0">
                        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-white/50">{title}</h4>
                        <p className="max-w-md text-sm leading-relaxed text-white/70">{body}</p>
                      </div>
                    </Reveal>
                  ))}
                  <Reveal delay={0.5}>
                    <div className="flex flex-col gap-3 border-t border-white/10 pt-8">
                      <h4 className="text-[10px] font-semibold uppercase tracking-widest text-white/50">Prefer Email?</h4>
                      <a href="mailto:careers@catalyst7.in" className="text-sm transition-colors hover:text-white/70">
                        careers@catalyst7.in
                      </a>
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open role */}
        <section id="roles" data-testid="careers-roles" className="px-6 py-16 md:px-10">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50">Open Role</p>
              <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-baseline sm:justify-between sm:gap-10">
                <h2 className="font-display text-4xl font-normal tracking-tight sm:text-5xl">AI Filmmaker</h2>
                <p className="max-w-md text-sm leading-relaxed text-white/50">
                  One role, many hats. We're looking for hybrid creators who can take an idea
                  from script to screen using both classic craft and generative AI.
                </p>
              </div>
            </Reveal>

            <div className="mt-12">
              <Reveal>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50">
                  What We're Looking For
                </p>
              </Reveal>
              <div className="mt-6 flex flex-col">
                {skills.map((s, i) => (
                  <Reveal key={s} delay={0.05 * i}>
                    <div className="flex items-baseline gap-6 border-t border-white/10 py-5 sm:gap-10 sm:px-4">
                      <span className="text-xs font-semibold text-white/30">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-base sm:text-lg">{s}</p>
                    </div>
                  </Reveal>
                ))}
                <div className="border-t border-white/10 pt-8 text-sm leading-relaxed text-white/50">
                  You don't need to tick every box — strength in a few of these plus the hunger
                  to learn the rest is enough.{" "}
                  <a href="#apply" className="text-white underline-offset-4 hover:underline">
                    Apply above
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

/* ── Friendly Field component ── */
const Field = ({ label, value, onChange, placeholder, type = "text", hint, required }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-medium text-white/70">
      {label}{required && <span className="text-white/30 ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-4 text-base outline-none transition-all placeholder:text-white/25 focus:border-white/40 focus:bg-white/[0.06]"
    />
    {hint && <p className="text-xs text-white/30">{hint}</p>}
  </div>
);

/* ── Friendly Select component ── */
const SelectField = ({ label, value, onChange, options, placeholder, required }) => (
  <div className="flex flex-col gap-2 relative">
    <label className="text-sm font-medium text-white/70">
      {label}{required && <span className="text-white/30 ml-1">*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      className={`w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-4 text-base outline-none transition-all focus:border-white/40 focus:bg-white/[0.06] appearance-none cursor-pointer ${!value ? "text-white/25" : "text-white"}`}
    >
      <option value="" disabled hidden>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt} className="text-black bg-white">{opt}</option>
      ))}
    </select>
    <div className="pointer-events-none absolute bottom-5 right-4 text-white/40">▾</div>
  </div>
);
